import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { User, onIdTokenChanged } from 'firebase/auth';
import { Login } from '@/pages/login';
import { Dashboard } from '@/pages/dashboard';
import { auth } from '@/lib/firebase';
import { BoxList } from './pages/boxes-list/boxList';
import { DashboardMenu } from '@/components/dashboardMenu';
import { ThemeProvider } from '@/components/theme-provider';
import { SettingsLayouts } from './pages/settings/layout';
import { AppearanceForm } from './pages/settings/appearance-form';
import { NotificationForm } from './pages/settings/notification-form';
import { TaskRunner } from './pages/task/task-runner';
import { ConfirmResetPassword } from './pages/confirm-reset-password';
import { ConnectionLayout } from './pages/connection-layout';
import { ResetPassword } from './pages/reset-password';
import TvControllersList from './pages/tv-controllers';
import CreateTvControllerPage from './pages/tv-controllers/create';
import UpdateTvControllerPage from './pages/tv-controllers/updateTvControllerPage';
import TvControllerGroups from './pages/tv-controllers/groups';
import { ChannelGuideList } from './pages/channel-guide';
import CreateChannelGuide from './pages/channel-guide/createChannelGuide';
import { UpdateChannelGuide } from './pages/channel-guide/updateChannelGuide';
import './globals.css';
import { CreateChannel } from './pages/channel-guide/channels/createChannel';
import { UpdateChannel } from './pages/channel-guide/channels/updateChannel';
import { Images } from './pages/images';
import CreateImage from './pages/images/createImage';
import { Stats } from './pages/stats';
import { Monitoring } from './pages/monitoring';
import { BoxHistory } from './pages/box-history';
import { Incidents } from './pages/incidents';
import { StadiumMap } from './pages/stadium-map';
import { TabletsLayout } from './pages/tablets/layout';
import { Tablets } from './pages/tablets';
import { TabletGroups } from './pages/tablets/groups';
import { Apks } from './pages/apks';
// import { Presets } from './pages/preset';
// import CreatePreset from './pages/preset/createPreset';
// import UpdatePreset from './pages/preset/updatePrset';

type UserAuthorization = {
  channelGuide: boolean;
  tvController: boolean;
  boxes: boolean;
  admin: boolean;
};

function SecureRoute({
  user,
  authorization,
}: {
  user: User | null;
  authorization: UserAuthorization;
}) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <DashboardMenu authorization={authorization} />;
}

function AuthorizedRoute({ isAuthorized }: { isAuthorized: boolean }) {
  if (!isAuthorized) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

// Landing page: users with the boxes claim get the live TV map; users with
// only the tvController claim go straight to the ZeeVee dashboard.
function Home({ authorization }: { authorization: UserAuthorization }) {
  if (authorization.boxes) {
    return <StadiumMap />;
  }
  if (authorization.tvController) {
    return <Navigate to="/tv-controllers" replace />;
  }
  return <Dashboard />;
}

function UnsecureRoute({ user }: { user: User | null }) {
  if (user) {
    return <Navigate to="/" replace />;
  }

  return <ConnectionLayout />;
}

const queryClient = new QueryClient();

function App({ tvController, channelGuide, boxes, admin }: UserAuthorization) {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [authorization, setAuthorization] = useState<UserAuthorization>({
    tvController,
    channelGuide,
    boxes,
    admin,
  });
  useEffect(() => {
    // onIdTokenChanged (not onAuthStateChanged) so the forced refresh after
    // /auth/sync-claims re-fires this listener and the new claims are applied
    // without a reload.
    const unsubscribe = onIdTokenChanged(auth, (user: User | null) => {
      if (user) {
        // No forced refresh here: main.tsx already force-refreshed the token at
        // startup, and a fresh sign-in mints a new token anyway. Forcing again
        // would just add a second network round-trip on every load.
        user
          .getIdTokenResult()
          .then((idTokenResult) => {
            // Access custom claims
            setAuthorization({
              tvController: !!idTokenResult.claims.tvController || false,
              channelGuide: !!idTokenResult.claims.channelGuide || false,
              boxes: !!idTokenResult.claims.boxes || false,
              admin: !!idTokenResult.claims.admin || false,
            });
            // You can store these claims in state if needed
          })
          .catch((error) => {
            console.error('Error fetching user claims:', error);
          });
      }
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <SecureRoute user={user} authorization={authorization} />
              }
            >
              <Route path="/settings" element={<SettingsLayouts />}>
                <Route
                  path="/settings/appearance"
                  element={<AppearanceForm />}
                />
                <Route
                  path="/settings/notification"
                  element={<NotificationForm />}
                />
              </Route>
              <Route
                path="/boxes"
                element={<AuthorizedRoute isAuthorized={authorization.boxes} />}
              >
                <Route path="/boxes/task/:taskId" element={<TaskRunner />} />
                <Route path="/boxes" element={<BoxList />} />
              </Route>
              <Route
                path="/tv-controllers"
                element={
                  <AuthorizedRoute isAuthorized={authorization.tvController} />
                }
              >
                <Route path="/tv-controllers" element={<TvControllersList />} />
                <Route
                  path="/tv-controllers/create"
                  element={<CreateTvControllerPage />}
                />
                <Route
                  path="/tv-controllers/groups"
                  element={<TvControllerGroups />}
                />
                <Route
                  path="/tv-controllers/:id"
                  element={<UpdateTvControllerPage />}
                />
                <Route
                  path="/tv-controllers/task/:taskId"
                  element={<TaskRunner />}
                />
              </Route>
              <Route
                path="/tablets"
                element={<AuthorizedRoute isAuthorized={authorization.admin} />}
              >
                <Route path="/tablets" element={<TabletsLayout />}>
                  <Route
                    index
                    element={<Navigate to="/tablets/groups" replace />}
                  />
                  <Route path="/tablets/list" element={<Tablets />} />
                  <Route path="/tablets/groups" element={<TabletGroups />} />
                </Route>
              </Route>
              <Route
                path="/channel-guides"
                element={
                  <AuthorizedRoute isAuthorized={authorization.channelGuide} />
                }
              >
                <Route path="/channel-guides" element={<ChannelGuideList />} />
                <Route
                  path="/channel-guides/create"
                  element={<CreateChannelGuide />}
                />
                <Route
                  path="/channel-guides/:id"
                  element={<UpdateChannelGuide />}
                />
                <Route
                  path="/channel-guides/:channelGuideId/channels"
                  element={<CreateChannel />}
                />
                <Route
                  path="/channel-guides/:channelGuideId/channels/:id"
                  element={<UpdateChannel />}
                />
              </Route>
              <Route
                path="/images"
                element={
                  <AuthorizedRoute isAuthorized={authorization.tvController} />
                }
              >
                <Route path="/images" element={<Images />} />
                <Route path="/images/create" element={<CreateImage />} />
              </Route>
              <Route
                path="/stats"
                element={<AuthorizedRoute isAuthorized={authorization.admin} />}
              >
                <Route path="/stats" element={<Stats />} />
              </Route>
              <Route
                path="/monitoring"
                element={
                  <AuthorizedRoute
                    isAuthorized={authorization.boxes || authorization.admin}
                  />
                }
              >
                <Route path="/monitoring" element={<Monitoring />} />
              </Route>
              <Route
                path="/incidents"
                element={<AuthorizedRoute isAuthorized={authorization.admin} />}
              >
                <Route path="/incidents" element={<Incidents />} />
              </Route>
              <Route
                path="/box-history"
                element={<AuthorizedRoute isAuthorized={authorization.admin} />}
              >
                <Route path="/box-history" element={<BoxHistory />} />
              </Route>
              <Route
                path="/apks"
                element={<AuthorizedRoute isAuthorized={authorization.admin} />}
              >
                <Route path="/apks" element={<Apks />} />
              </Route>
              {/* <Route
                path="/presets"
                element={
                  <AuthorizedRoute isAuthorized={authorization.tvController} />
                }
              >
                <Route path="/presets" element={<Presets />} />
                <Route path="/presets/create" element={<CreatePreset />} />
                <Route path="/presets/:id" element={<UpdatePreset />} />
              </Route> */}
              <Route index element={<Home authorization={authorization} />} />
            </Route>
            <Route element={<UnsecureRoute user={user} />}>
              <Route path="/login" element={<Login />} />
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/confirm-reset" element={<ConfirmResetPassword />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
